import React, { useState, forwardRef, useEffect, useCallback, useRef } from 'react';
import { Range } from 'react-range';
import './FilterComponent.css';
import { AiOutlineClose } from 'react-icons/ai';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const FilterComponent = forwardRef(({ onFilter, isOpen, handleFilterClose, query }, ref) => {
    const { t } = useTranslation();
    const [values, setValues] = useState([0, 20000]);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [selectedLevel, setSelectedLevel] = useState('');
    const [rating, setRating] = useState(0);
    const [showMoreLanguages, setShowMoreLanguages] = useState(false);
    const [showLevelOptions, setShowLevelOptions] = useState(false);
    // const [showRatingOptions, setShowRatingOptions] = useState(false);
    const [showPriceRange, setShowPriceRange] = useState(false);
    const [showLanguages, setShowLanguages] = useState(true);

    const languages = ['English', 'Telugu', 'Hindi'];
    const levels = ['Beginner', 'Intermediate', 'Advanced'];

    const debounceTimeout = useRef(null);
    const prevFilters = useRef({ values, selectedLanguage, selectedLevel, rating, query });

    const handleFilter = useCallback(() => {
        if (
            prevFilters.current.values === values &&
            prevFilters.current.selectedLanguage === selectedLanguage &&
            prevFilters.current.selectedLevel === selectedLevel &&
            prevFilters.current.rating === rating &&
            prevFilters.current.query === query
        ) {
            return;
        }

        const filters = {
            ...(values[0] > 0 && { minPrice: values[0] }),
            ...(values[1] < 20000 && { maxPrice: values[1] }),
            ...(selectedLanguage && { language: selectedLanguage }),
            ...(selectedLevel && { level: selectedLevel }),
            ...(rating > 0 && { rating }),
            searchTerm: query,
        };

        onFilter(filters);

        prevFilters.current = { values, selectedLanguage, selectedLevel, rating, query };
    }, [values, selectedLanguage, selectedLevel, rating, query, onFilter]);

    useEffect(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            handleFilter();
        }, 300);

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [values, selectedLanguage, selectedLevel, rating, handleFilter]);

    const clearFilters = () => {
        toast.dismiss();

        setValues([0, 20000]);
        setSelectedLanguage('');
        setSelectedLevel('');
        setRating(0);
        handleFilter();
        toast.success("Filters cleared successfully!");
    };

    const handleOptionSelect = () => {
        if (window.innerWidth <= 786) {
            handleFilterClose();
        }
    };

    return (
        <div className={`filter-container ${isOpen ? 'open' : 'closed'}`} ref={ref}>
            {isOpen && (
                <div className="filter-sidebar">
                    <div className="close-icon" onClick={handleFilterClose}>
                        <AiOutlineClose size={20} />
                    </div>

                    <div className='subsidebar'>
                        <div onClick={() => {
                            setShowMoreLanguages(false);
                            setShowLevelOptions(false);
                            // setShowRatingOptions(false);
                            setShowPriceRange(false);
                            setShowLanguages(!showLanguages);
                        }} className='toggle-label'>
                            <span>{t('Language')}</span>
                            <span>{showLanguages ? '▲' : '▼'}</span>
                        </div>
                        {showLanguages && (
                            <>
                                {languages.slice(0, showMoreLanguages ? languages.length : 3).map((lang) => (
                                    <div key={lang}>
                                        <label>
                                            <input
                                                className='filter-radio'
                                                type="radio"
                                                value={lang}
                                                checked={selectedLanguage === lang}
                                                onChange={() => {
                                                    setSelectedLanguage(lang);
                                                    handleOptionSelect();
                                                }}
                                            />
                                            {lang}
                                        </label>
                                    </div>
                                ))}
                                <p className='showmorebutton' onClick={() => setShowMoreLanguages(!showMoreLanguages)}>
                                    {/* {showMoreLanguages ? t('ShowLess') : t('ShowMore')} */}
                                </p>
                            </>
                        )}
                    </div>

                    <div className='subsidebar'>
                        <div onClick={() => {
                            setShowMoreLanguages(false);
                            setShowLevelOptions(!showLevelOptions);
                            // setShowRatingOptions(false);
                            setShowPriceRange(false);
                            setShowLanguages(false);
                        }} className='toggle-label'>
                            <span>{t('Level')}</span>
                            <span>{showLevelOptions ? '▲' : '▼'}</span>
                        </div>
                        {showLevelOptions && levels.map((lvl) => (
                            <div key={lvl}>
                                <label>
                                    <input
                                        className='filter-radio'
                                        type="radio"
                                        value={lvl}
                                        checked={selectedLevel === lvl}
                                        onChange={() => {
                                            setSelectedLevel(lvl);
                                            handleOptionSelect();
                                        }}
                                    />
                                    {lvl}
                                </label>
                            </div>
                        ))}
                    </div>

                    <div className='subsidebar'>
                        <div onClick={() => {
                            setShowMoreLanguages(false);
                            setShowLevelOptions(false);
                            // setShowRatingOptions(false);
                            setShowPriceRange(!showPriceRange);
                            setShowLanguages(false);
                        }} className='toggle-label'>
                            <span>{t('PriceRange')}</span>
                            <span>{showPriceRange ? '▲' : '▼'}</span>
                        </div>
                        {showPriceRange && (
                            <>
                                <div className="range-slider">
                                    <Range
                                        values={values}
                                        step={1}
                                        min={0}
                                        max={20000}
                                        onChange={setValues}
                                        renderTrack={({ props, children }) => {
                                            const [min, max] = values;
                                            return (
                                                <div {...props} className="track">
                                                    <div
                                                        className="selected-track"
                                                        style={{
                                                            left: `${(min / 20000) * 100}%`,
                                                            width: `${((max - min) / 20000) * 100}%`,
                                                        }}
                                                    />
                                                    {children}
                                                </div>
                                            );
                                        }}
                                        renderThumb={({ props }) => (
                                            <div {...props} className="thumb" />
                                        )}
                                    />
                                </div>
                                <div>
                                    ₹{values[0]} -  ₹{values[1]}
                                </div>
                            </>
                        )}
                    </div>

                    {/* <div className='subsidebar'>
                        <div onClick={() => {
                            setShowMoreLanguages(false);
                            setShowLevelOptions(false);
                            setShowRatingOptions(!showRatingOptions);
                            setShowPriceRange(false);
                            setShowLanguages(false);
                        }} className='toggle-label'>
                            <span>{t('Rating')}</span>
                            <span>{showRatingOptions ? '▲' : '▼'}</span>
                        </div>
                        {showRatingOptions && (
                            <>
                                {[5, 4, 3, 2, 1].map((rate) => (
                                    <div key={rate}>
                                        <label>
                                            <input
                                                className='filter-radio'
                                                type="radio"
                                                value={rate}
                                                checked={rating === rate}
                                                onChange={() => {
                                                    setRating(rate);
                                                    handleOptionSelect();
                                                }}
                                            />
                                            {'★'.repeat(rate)}{'☆'.repeat(5 - rate)}
                                        </label>
                                    </div>
                                ))}
                            </>
                        )}
                    </div> */}

                    <button className='apply-filter-button' onClick={clearFilters}>{t('ClearFilters')}</button>
                </div>
            )}
        </div>
    );
});

export default FilterComponent;
